/**
 * React hook and component for automatic page tracking with GTM
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { analytics } from './analytics';
import { useAuth } from '../contexts/AuthContext';

// Page name mapping for cleaner analytics
const PAGE_NAMES: Record<string, string> = {
    '/': 'Landing Page',
    '/login': 'Login',
    '/register': 'Register',
    '/onboarding': 'Onboarding',
    '/forgot-password': 'Forgot Password',
    '/reset-password': 'Reset Password',
    '/dashboard': 'Dashboard',
    '/calendar': 'Calendar',
    '/properties': 'Properties',
    '/bookings': 'Bookings',
    '/tasks': 'Tasks',
    '/tickets': 'Tickets',
    '/employees': 'Employees',
    '/owners': 'Owners',
    '/owner/dashboard': 'Owner Dashboard',
    '/integrations': 'Integrations',
    '/admin/users': 'Admin Users',
    '/users': 'Users',
    '/task-templates': 'Task Templates',
    '/messages': 'Messages',
    '/monitoring': 'Monitoring',
    '/admin/logs': 'Admin Logs',
    '/import-properties': 'Property Import',
    '/settings': 'Settings',
    '/profile': 'Profile',
    '/notifications': 'Notifications',
    '/calendar-export': 'Calendar Export',
    '/analytics': 'Analytics Dashboard',
    '/inventory': 'Inventory',
    '/map': 'Map',
    '/welcome-books': 'Welcome Books',
    '/invoices': 'Invoices',
    '/billing': 'Billing',
    '/schedule': 'Employee Schedule',
    '/privacy': 'Privacy Policy',
    '/terms': 'Terms of Service',
};

/**
 * Get a clean page name from a URL path
 */
function getPageName(pathname: string): string {
    // Check for exact match
    if (PAGE_NAMES[pathname]) {
        return PAGE_NAMES[pathname];
    }

    // Check for dynamic routes
    if (pathname.startsWith('/owner/properties/')) {
        return 'Property Details (Owner)';
    }
    if (pathname.startsWith('/welcome/')) {
        return 'Guest Welcome Book';
    }
    if (pathname.startsWith('/guest/guide/')) {
        return 'Guest Guide';
    }
    if (pathname.startsWith('/activate/')) {
        return 'Account Activation';
    }
    if (pathname.startsWith('/auth/callback/')) {
        return 'Auth Callback';
    }

    // Fallback: convert path to title case
    return pathname
        .replace(/^\//, '')
        .replace(/-/g, ' ')
        .replace(/\//g, ' > ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ') || 'Unknown Page';
}

/**
 * Hook to track page views automatically on route changes
 */
export function usePageTracking(): void {
    const location = useLocation();
    const { user, isAuthenticated } = useAuth();
    const previousPath = useRef<string>('');

    useEffect(() => {
        // Only track if the path actually changed
        if (location.pathname === previousPath.current) {
            return;
        }

        previousPath.current = location.pathname;

        const pageName = getPageName(location.pathname);
        const fullUrl = `${window.location.origin}${location.pathname}${location.search}`;

        // Track the page view
        analytics.trackPageView(pageName, fullUrl);

        // If user is authenticated, set user properties
        if (isAuthenticated && user) {
            analytics.setUserProperties({
                userId: user.id?.toString(),
                userRole: user.role,
                tenantId: user.tenant_id?.toString(),
            });
        }
    }, [location.pathname, location.search, isAuthenticated, user]);
}

/**
 * Component wrapper that enables page tracking
 * Place this inside your Router but at the top level of your app content
 */
export function PageTracker(): null {
    usePageTracking();
    return null;
}

export default PageTracker;
